import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useKlantenStore, type OpFactuurOrdersUitkomst } from '../../stores/klanten-store';

interface Props {
  accountName: string;
  /** Huidige stand: klant betaalt altijd op rekening (Betalingswijze 'Factuur'). */
  actief: boolean;
  betalingstermijn?: string | null;
  canEdit: boolean;
}

/**
 * Klantkaart-vlag "Betaalt op factuur" (zakelijke accounts). Aan = elke nieuwe
 * Wasserij-order krijgt Op_Factuur automatisch (geen betaallink, geen
 * herinneringen, factuur achteraf) en de lopende orders volgen direct mee.
 * Uit = standaard betaalflow voor nieuwe orders; bestaande orders blijven
 * zoals ze staan (per order terugzetten via de Betaling-card).
 * Server: POST /api/customers/account-update, veld Betalingswijze__c;
 * runbook office-app/docs/api/werkorder-op-factuur.md §Account-default.
 */
export function OpFactuurKlantToggle({ accountName, actief, betalingstermijn, canEdit }: Props) {
  const updateCustomerAccount = useKlantenStore((s) => s.updateCustomerAccount);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uitkomst, setUitkomst] = useState<OpFactuurOrdersUitkomst | null>(null);
  const [melding, setMelding] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!melding) return;
    const t = window.setTimeout(() => setMelding(null), 8000);
    return () => window.clearTimeout(t);
  }, [melding]);

  async function bevestig() {
    setSubmitting(true);
    setError(null);
    const res = await updateCustomerAccount({ betaaltOpFactuur: !actief });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || 'Opslaan mislukt');
      return;
    }
    setOpen(false);
    if (!actief) {
      const u = res.opFactuurOrders ?? { gezet: [], overgeslagen: [] };
      setUitkomst(u);
      const n = u.gezet.length;
      setMelding(
        n === 0
          ? 'Klant betaalt voortaan op factuur. Geen lopende orders om bij te werken.'
          : `Klant betaalt voortaan op factuur. ${n} lopende order${n === 1 ? '' : 's'} op factuur gezet${u.overgeslagen.length ? `, ${u.overgeslagen.length} overgeslagen` : ''}.`
      );
    } else {
      setUitkomst(null);
      setMelding('Nieuwe orders volgen weer de standaard betaalflow. Lopende orders zijn niet gewijzigd.');
    }
  }

  return (
    <>
      <span className="flex items-center gap-1.5">
        {actief ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
            title={`Betaalt altijd op rekening${betalingstermijn ? ` (termijn ${betalingstermijn})` : ''}. Nieuwe orders krijgen automatisch "Op factuur": geen betaallink, geen herinneringen.`}
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Betaalt op factuur
          </span>
        ) : null}
        {canEdit && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={clsx('text-xs underline', actief ? 'text-slate-400 hover:text-slate-600' : 'text-indigo-600 hover:text-indigo-700')}
            title={actief ? 'Nieuwe orders weer via de standaard betaalflow' : 'Klant betaalt altijd op rekening: elke nieuwe order op factuur'}
          >
            {actief ? 'uitzetten' : 'Betaalt op factuur?'}
          </button>
        )}
        {melding && <span className="text-xs text-emerald-700">{melding}</span>}
      </span>
      {uitkomst && uitkomst.overgeslagen.length > 0 && (
        <span className="text-xs text-amber-700">
          Overgeslagen: {uitkomst.overgeslagen.map((o) => `${o.naam ?? o.sfId} (${o.reden})`).join('; ')}
        </span>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
          onClick={() => { if (!submitting) setOpen(false); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-900">
                {actief ? 'Op factuur uitzetten' : 'Klant betaalt op factuur'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-slate-700">
              {actief ? (
                <p>
                  Nieuwe orders van <span className="font-medium">{accountName}</span> volgen hierna weer de
                  standaard betaalflow (betaallink vooraf, pin of contant bij aflevering). Lopende orders
                  die al op factuur staan blijven zo; zet die per order terug als dat nodig is.
                </p>
              ) : (
                <>
                  <p>
                    <span className="font-medium">{accountName}</span> betaalt voortaan altijd op rekening.
                    Elke nieuwe wasserij-order krijgt automatisch "Op factuur achteraf", en de lopende orders
                    die nog niet gefactureerd of betaald zijn gaan nu direct mee.
                  </p>
                  <ul className="list-disc pl-5 space-y-0.5 text-slate-600">
                    <li>Geen Mollie-betaallink en geen iDEAL-knop in de mails</li>
                    <li>Chauffeur vraagt niet om pin of contant</li>
                    <li>Factuur achteraf per e-mail{betalingstermijn ? `, termijn ${betalingstermijn}` : ''}</li>
                    <li>Geen automatische betalingsherinneringen</li>
                  </ul>
                </>
              )}
              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="px-4 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={bevestig}
                disabled={submitting}
                className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                {submitting ? 'Bezig…' : actief ? 'Uitzetten' : 'Op factuur zetten'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
